import { clstx } from '@payaca/utilities/cvaMerge';
import { motion } from 'motion/react';
import { ComponentPropsWithoutRef, forwardRef } from 'react';
import UntitledLoading from '../UntitledLoading/UntitledLoading';
import useButtonClassName, { ButtonVariantProps } from './useButtonClassName';

export type ButtonProps = ComponentPropsWithoutRef<'button'> &
  ButtonVariantProps & {
    isProcessing?: boolean;
    replaceContentWhenProcessing?: boolean;
  };

/**
 * The standard action button. Use it to trigger an action on the current page.
 *
 * ## When to use
 *
 * - Submitting a form
 * - Opening a Modal or Sidebar
 * - Triggering a mutation (save, delete, add)
 *
 * ## When to choose another component
 *
 * - **Navigation** — use `AnchorButton`. Never use a Button styled as a link to
 *   navigate. See the "Links vs Buttons" section in
 *   `docs/technologies/ambrosia-design-system.md`.
 * - **A button that opens a menu** — use `ButtonDropdown`.
 * - **Row-level actions in a table** — use the `itemActions` prop on
 *   `ManageableItemsList.Table`, not Buttons in cells.
 *
 * ## Composition rules
 *
 * A Button has exactly one of these three layouts:
 *
 * 1. Text only
 * 2. An icon to the **left** of the text
 * 3. An icon only — this needs an accessible name
 *
 * Never place an icon to the right of the text.
 *
 * ✅ Icon before text:
 *
 * ```tsx
 * <Button>
 *   <UntitledIcon name="download-01.3" className="mr-2 h-5 w-5" />
 *   <span>Download template</span>
 * </Button>
 * ```
 *
 * ✅ Icon only, with an accessible name:
 *
 * ```tsx
 * <Button aria-label="Add customer" variant="secondary" size="xs">
 *   <UntitledIcon name="plus.3" className="h-3.5 w-3.5" />
 * </Button>
 * ```
 *
 * ❌ Icon after text:
 *
 * ```tsx
 * <Button>
 *   Remove
 *   <UntitledIcon name="x.3" />
 * </Button>
 * ```
 *
 * Further rules:
 *
 * - Pick the lowest-emphasis `variant` that still communicates importance. Use
 *   at most one `primary` Button per view.
 * - Use `colour` to signal intent: `positive` (green) for confirmations,
 *   `negative` (red) for destructive actions. `job-brand` follows the Account's
 *   brand colour on customer-facing surfaces, and falls back to the primary
 *   purple for an Account that has not set one.
 * - Set `isProcessing` while an async action is in flight. Add
 *   `replaceContentWhenProcessing` to swap the label for the spinner instead of
 *   showing both.
 *
 * ## Accessibility
 *
 * Icon-only Buttons must have an accessible name: an `aria-label`, or a visually
 * hidden `sr-only` span.
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    className,
    type = 'button',
    variant,
    size,
    colour,
    linkColour,
    children,
    isProcessing = false,
    replaceContentWhenProcessing = false,
    ...rest
  } = props;

  const variantClassName = useButtonClassName({
    variant,
    size,
    colour,
    linkColour,
  });

  return (
    <button
      ref={ref}
      type={type}
      className={clstx(variantClassName, 'relative', className)}
      {...rest}
    >
      {!(replaceContentWhenProcessing && isProcessing) && children}
      {isProcessing && <UntitledLoading className="h-4 w-4" />}
    </button>
  );
});

export default Object.assign(Button, {
  motion: motion.create(Button),
});
